import { throttle as v } from "lodash-es";
import B from "./BaseRenderer.js";
import { createWebGL2WarpedMapFactory as j } from "../maps/WebGL2WarpedMap.js";
import X from "../tilecache/CacheableImageBitmapTile.js";
import { squaredDistance as $, hexToFractionalRgb as u, maxOfNumberOrUndefined as E } from "@allmaps/stdlib";
import { supportedDistortionMeasures as k } from "@allmaps/transform";
import { red as R, darkblue as Y, green as J, yellow as K, black as Q } from "@allmaps/tailwind";
import { WarpedMapEvent as l, WarpedMapEventType as M } from "../shared/events.js";
import { createTransform as Z, invertTransform as tt, multiplyTransform as et, transformToMatrix4 as g } from "../shared/matrix.js";
import { createShader as h, createProgram as T } from "../shared/webgl2.js";
import _ from "../shaders/maps/vertex-shader.glsl.js";
import S from "../shaders/maps/fragment-shader.glsl.js";
import C from "../shaders/lines/vertex-shader.glsl.js";
import A from "../shaders/lines/fragment-shader.glsl.js";
import P from "../shaders/points/vertex-shader.glsl.js";
import I from "../shaders/points/fragment-shader.glsl.js";
const rt = 200, ot = {
  leading: !0,
  trailing: !0
}, it = 50, nt = {
  leading: !0,
  trailing: !0
}, O = 1, w = 1, st = 0, at = 0.7, pt = 5, F = 750;
class St extends B {
  gl;
  mapsProgram;
  pointsProgram;
  linesProgram;
  previousSignificantViewport;
  opacity = O;
  saturation = w;
  renderOptions = {};
  invertedRenderTransform;
  lastAnimationFrameRequestId;
  animating = !1;
  transformationTransitionStart;
  animationProgress = 0;
  disableRender = !1;
  throttledPrepareRenderInternal;
  throttledChanged;
  /**
   * Creates an instance of WebGL2Renderer.
   *
   * @constructor
   * @param {WebGL2RenderingContext} gl - WebGL 2 rendering context
   * @param {WebGL2RendererOptions} options - options
   */
  constructor(t, e) {
    const r = h(
      t,
      t.VERTEX_SHADER,
      _
    ), o = h(
      t,
      t.FRAGMENT_SHADER,
      S
    ), i = h(
      t,
      t.VERTEX_SHADER,
      P
    ), p = h(
      t,
      t.FRAGMENT_SHADER,
      I
    ), n = h(
      t,
      t.VERTEX_SHADER,
      C
    ), a = h(
      t,
      t.FRAGMENT_SHADER,
      A
    ), d = T(t, r, o), c = T(
      t,
      i,
      p
    ), m = T(
      t,
      n,
      a
    );
    super(
      X.createFactory(),
      j(
        t,
        d,
        c,
        m
      ),
      e
    ), this.gl = t, this.mapsProgram = d, this.pointsProgram = c, this.linesProgram = m, t.deleteShader(r), t.deleteShader(o), t.deleteShader(r), t.deleteShader(o), t.deleteShader(r), t.deleteShader(o), t.disable(t.DEPTH_TEST), this.invertedRenderTransform = Z(), this.addEventListeners(), this.throttledPrepareRenderInternal = v(
      this.prepareRenderInternal.bind(this),
      rt,
      ot
    ), this.throttledChanged = v(
      this.changed.bind(this),
      it,
      nt
    );
  }
  initializeWebGL(t) {
    const e = h(
      t,
      t.VERTEX_SHADER,
      _
    ), r = h(
      t,
      t.FRAGMENT_SHADER,
      S
    ), o = h(
      t,
      t.VERTEX_SHADER,
      P
    ), i = h(
      t,
      t.FRAGMENT_SHADER,
      I
    ), p = h(
      t,
      t.VERTEX_SHADER,
      C
    ), n = h(
      t,
      t.FRAGMENT_SHADER,
      A
    ), a = T(t, e, r), d = T(
      t,
      o,
      i
    ), c = T(
      t,
      p,
      n
    );
    this.gl = t, this.mapsProgram = a, this.pointsProgram = d, this.linesProgram = c, t.disable(t.DEPTH_TEST);
    for (const m of this.warpedMapList.getWarpedMaps())
      m.initializeWebGL(a, d, c);
  }
  /**
   * Get the opacity of the renderer
   *
   * @returns {(number | undefined)}
   */
  getOpacity() {
    return this.opacity;
  }
  /**
   * Set the opacity of the renderer
   *
   * @param {number} opacity - opacity to set
   */
  setOpacity(t) {
    this.opacity = t;
  }
  /**
   * Reset the opacity of the renderer
   */
  resetOpacity() {
    this.opacity = O;
  }
  /**
   * Get the opacity of a map
   *
   * @param {string} mapId - ID of the map
   * @returns {(number | undefined)}
   */
  getMapOpacity(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    if (e)
      return e.opacity;
  }
  /**
   * Set the opacity of a map
   *
   * @param {string} mapId - ID of the map
   * @param {number} opacity - opacity to set
   */
  setMapOpacity(t, e) {
    const r = this.warpedMapList.getWarpedMap(t);
    r && (r.opacity = Math.min(Math.max(e, 0), 1));
  }
  /**
   * Rreset the opacity of a map
   *
   * @param {string} mapId - ID of the map
   */
  resetMapOpacity(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    e && (e.opacity = O);
  }
  /**
   * Get the remove color options of the renderer
   *
   * @returns {(Partial<RemoveColorOptions> | undefined)}
   */
  getRemoveColorOptions() {
    return this.renderOptions.removeColorOptions;
  }
  /**
   * Set the remove color options of the renderer
   *
   * @param {RemoveColorOptions} removeColorOptions
   */
  setRemoveColorOptions(t) {
    this.renderOptions.removeColorOptions = t;
  }
  /**
   * Reset the remove color options of the renderer
   */
  resetRemoveColorOptions() {
    this.renderOptions.removeColorOptions = void 0;
  }
  /**
   * Get the remove color options of a map
   *
   * @param {string} mapId - ID of the map
   * @returns {(Partial<RemoveColorOptions> | undefined)}
   */
  getMapRemoveColorOptions(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    if (e)
      return e.renderOptions.removeColorOptions;
  }
  /**
   * Set the remove color options of a map
   *
   * @param {string} mapId - ID of the map
   * @param {RemoveColorOptions} removeColorOptions - the 'remove color options' to set
   */
  setMapRemoveColorOptions(t, e) {
    const r = this.warpedMapList.getWarpedMap(t);
    r && (r.renderOptions.removeColorOptions = e);
  }
  /**
   * Reset the remove color options of a map
   *
   * @param {string} mapId - ID of the map
   */
  resetMapRemoveColorOptions(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    e && (e.renderOptions.removeColorOptions = void 0);
  }
  /**
   * Get the colorize options of the renderer
   *
   * @returns {(Partial<ColorizeOptions> | undefined)}
   */
  getColorizeOptions() {
    return this.renderOptions.colorizeOptions;
  }
  /**
   * Set the colorize options of the renderer
   *
   * @param {ColorizeOptions} colorizeOptions - the colorize options to set
   */
  setColorizeOptions(t) {
    this.renderOptions.colorizeOptions = t;
  }
  /**
   * Reset the colorize options of the renderer
   */
  resetColorizeOptions() {
    this.renderOptions.colorizeOptions = void 0;
  }
  /**
   * Get the colorize options of a map
   *
   * @param {string} mapId - ID of the map
   * @returns {(Partial<ColorizeOptions> | undefined)}
   */
  getMapColorizeOptions(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    if (e)
      return e.renderOptions.colorizeOptions;
  }
  /**
   * Set the colorize options of a map
   *
   * @param {string} mapId - ID of the map
   * @param {ColorizeOptions} colorizeOptions - the colorize options to set
   */
  setMapColorizeOptions(t, e) {
    const r = this.warpedMapList.getWarpedMap(t);
    r && (r.renderOptions.colorizeOptions = e);
  }
  /**
   * Reset the colorize options of a map
   *
   * @param {string} mapId - ID of the map
   */
  resetMapColorizeOptions(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    e && (e.renderOptions.colorizeOptions = void 0);
  }
  /**
   * Get the grid options of the renderer
   *
   * @returns {(Partial<GridOptions> | undefined)}
   */
  getGridOptions() {
    return this.renderOptions.gridOptions;
  }
  /**
   * Set the grid options of the renderer
   *
   * @param {GridOptions} gridOptions - the grid options to set
   */
  setGridOptions(t) {
    this.renderOptions.gridOptions = t;
  }
  /**
   * Reset the grid options of the renderer
   */
  resetGridOptions() {
    this.renderOptions.gridOptions = void 0;
  }
  /**
   * Get the grid options of a map
   *
   * @param {string} mapId - ID of the map
   * @returns {(Partial<GridOptions> | undefined)}
   */
  getMapGridOptions(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    if (e)
      return e.renderOptions.gridOptions;
  }
  /**
   * Set the grid options of a map
   *
   * @param {string} mapId - ID of the map
   * @param {GridOptions} gridOptions - the grid options to set
   */
  setMapGridOptions(t, e) {
    const r = this.warpedMapList.getWarpedMap(t);
    r && (r.renderOptions.gridOptions = e);
  }
  /**
   * Reset the grid options of a map
   *
   * @param {string} mapId - ID of the map
   */
  resetMapGridOptions(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    e && (e.renderOptions.gridOptions = void 0);
  }
  /**
   * Get the saturation of the renderer
   *
   * @returns {number}
   */
  getSaturation() {
    return this.saturation;
  }
  /**
   * Set the saturation of the renderer
   *
   * 0 - grayscale, 1 - original colors
   *
   * @param saturation - the satuation to set
   */
  setSaturation(t) {
    this.saturation = t;
  }
  /**
   * Reset the satuation of the renderer
   */
  resetSaturation() {
    this.saturation = w;
  }
  /**
   * Get the saturation of a map
   *
   * @param {string} mapId - ID of the map
   * @returns {(number | undefined)}
   */
  getMapSaturation(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    if (e)
      return e.saturation;
  }
  /**
   * Set the saturation of a map
   *
   * 0 - grayscale, 1 - original colors
   *
   * @param mapId - ID of the map
   * @param saturation - the saturation to set
   */
  setMapSaturation(t, e) {
    const r = this.warpedMapList.getWarpedMap(t);
    r && (r.saturation = e);
  }
  /**
   * Reset the saturation of a map
   *
   * @param {string} mapId - ID of the map
   */
  resetMapSaturation(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    e && (e.saturation = w);
  }
  /**
   * Render the map for a given viewport
   *
   * @param {Viewport} viewport - the current viewport
   */
  render(t) {
    this.disableRender || (this.viewport = t, this.loadMissingImageInfosInViewport(), this.someImageInfosInViewport() && this.throttledPrepareRenderInternal(), this.renderInternal());
  }
  clear() {
    this.warpedMapList.clear(), this.mapsInViewport = /* @__PURE__ */ new Set(), this.mapsWithRequestedTilesForViewport = /* @__PURE__ */ new Set(), this.gl.clear(this.gl.DEPTH_BUFFER_BIT | this.gl.COLOR_BUFFER_BIT), this.tileCache.clear();
  }
  cancelThrottledFunctions() {
    this.throttledPrepareRenderInternal.cancel(), this.throttledChanged.cancel();
  }
  destroy() {
    this.cancelThrottledFunctions();
    for (const t of this.warpedMapList.getWarpedMaps())
      this.removeEventListenersFromWebGL2WarpedMap(t);
    this.removeEventListeners(), super.destroy(), this.gl.deleteProgram(this.mapsProgram);
  }
  prepareRenderInternal() {
    this.requestFetchableTiles(), this.updateVertexBuffers();
  }
  shouldRequestFetchableTiles() {
    if (!this.viewport || this.animating)
      return !1;
    if (this.previousSignificantViewport) {
      const t = [];
      for (let r = 0; r < 4; r++)
        t.push(
          $(
            this.previousSignificantViewport.projectedGeoRectangle[r],
            this.viewport.projectedGeoRectangle[r]
          ) / Math.pow(this.viewport.projectedGeoPerViewportScale, 2)
        );
      const e = Math.max(...t);
      return e === 0 ? !0 : e > Math.pow(pt, 2) ? (this.previousSignificantViewport = this.viewport, !0) : !1;
    } else
      return this.previousSignificantViewport = this.viewport, !0;
  }
  shouldAnticipateInteraction() {
    return !0;
  }
  updateVertexBuffers() {
    if (this.viewport) {
      this.invertedRenderTransform = tt(
        this.viewport.projectedGeoToClipTransform
      );
      for (const t of this.mapsWithRequestedTilesForViewport) {
        const e = this.warpedMapList.getWarpedMap(t);
        if (!e)
          break;
        e.updateVertexBuffers(this.viewport.projectedGeoToClipTransform);
      }
    }
  }
  renderInternal() {
    if (!this.viewport)
      return;
    const t = this.gl;
    t.viewport(0, 0, t.canvas.width, t.canvas.height), t.enable(t.BLEND), t.blendFunc(t.ONE, t.ONE_MINUS_SRC_ALPHA), this.renderMapsInternal(), this.renderLinesInternal(), this.renderPointsInternal();
  }
  renderMapsInternal() {
    if (!this.viewport)
      return;
    const t = et(
      this.viewport.projectedGeoToClipTransform,
      this.invertedRenderTransform
    ), e = this.gl, r = this.mapsProgram;
    e.useProgram(r);
    const o = e.getUniformLocation(r, "u_debug");
    e.uniform1f(o, 0);
    const i = e.getUniformLocation(
      r,
      "u_renderTransform"
    );
    e.uniformMatrix4fv(
      i,
      !1,
      g(t)
    );
    const p = e.getUniformLocation(
      r,
      "u_animationProgress"
    );
    e.uniform1f(p, this.animationProgress);
    const n = e.getUniformLocation(
      r,
      "u_colorDistortion00"
    );
    e.uniform4f(n, ...u(R), 1);
    const a = e.getUniformLocation(
      r,
      "u_colorDistortion01"
    );
    e.uniform4f(a, ...u(Y), 1);
    const d = e.getUniformLocation(
      r,
      "u_colorDistortion1"
    );
    e.uniform4f(d, ...u(J), 1);
    const c = e.getUniformLocation(
      r,
      "u_colorDistortion2"
    );
    e.uniform4f(c, ...u(K), 1);
    const m = e.getUniformLocation(
      r,
      "u_colorDistortion3"
    );
    e.uniform4f(m, ...u(R), 1);
    const f = e.getUniformLocation(r, "u_colorGrid");
    e.uniform4f(f, ...u(Q), 1);
    for (const L of this.mapsWithRequestedTilesForViewport) {
      const s = this.warpedMapList.getWarpedMap(L);
      if (!s)
        continue;
      this.setRenderOptionsUniforms(this.renderOptions, s.renderOptions);
      const D = e.getUniformLocation(r, "u_opacity");
      e.uniform1f(D, this.opacity * s.opacity);
      const U = e.getUniformLocation(r, "u_saturation");
      e.uniform1f(U, this.saturation * s.saturation);
      const W = e.getUniformLocation(r, "u_distortion");
      if (e.uniform1f(W, s.distortionMeasure ? 1 : 0), s.distortionMeasure) {
        const q = e.getUniformLocation(r, "u_distortionOptionsdistortionMeasure");
        e.uniform1i(
          q,
          k.indexOf(s.distortionMeasure)
        );
      }
      const x = e.getUniformLocation(
        r,
        "u_currentBestScaleFactor"
      ), V = s.currentBestScaleFactor;
      e.uniform1i(x, V);
      const G = e.getUniformLocation(
        r,
        "u_cachedTilesTextureArray"
      );
      e.uniform1i(G, 0), e.activeTexture(e.TEXTURE0), e.bindTexture(e.TEXTURE_2D_ARRAY, s.cachedTilesTextureArray);
      const b = e.getUniformLocation(
        r,
        "u_cachedTilesResourcePositionsAndDimensionsTexture"
      );
      e.uniform1i(b, 2), e.activeTexture(e.TEXTURE2), e.bindTexture(
        e.TEXTURE_2D,
        s.cachedTilesResourcePositionsAndDimensionsTexture
      );
      const y = e.getUniformLocation(
        r,
        "u_cachedTilesScaleFactorsTexture"
      );
      e.uniform1i(y, 3), e.activeTexture(e.TEXTURE3), e.bindTexture(e.TEXTURE_2D, s.cachedTilesScaleFactorsTexture);
      const N = s.resourceTrianglePoints.length, H = this.gl.TRIANGLES, z = 0;
      e.bindVertexArray(s.mapsVao), e.drawArrays(H, z, N);
    }
  }
  renderLinesInternal() {
    if (!this.viewport)
      return;
    const t = this.gl, e = this.linesProgram;
    t.useProgram(e);
    const r = t.getUniformLocation(
      e,
      "u_projectedGeoToViewportTransform"
    );
    t.uniformMatrix4fv(
      r,
      !1,
      g(this.viewport.projectedGeoToViewportTransform)
    );
    const o = t.getUniformLocation(
      e,
      "u_viewportToClipTransform"
    );
    t.uniformMatrix4fv(
      o,
      !1,
      g(this.viewport.viewportToClipTransform)
    );
    const i = t.getUniformLocation(
      e,
      "u_animationProgress"
    );
    t.uniform1f(i, this.animationProgress);
    for (const p of this.mapsWithRequestedTilesForViewport) {
      const n = this.warpedMapList.getWarpedMap(p);
      if (!n)
        continue;
      const a = n.lineLayers.reduce(
        (m, f) => m + f.projectedGeoLines.length,
        0
      ) * 6, d = this.gl.TRIANGLES, c = 0;
      t.bindVertexArray(n.linesVao), t.drawArrays(d, c, a);
    }
  }
  renderPointsInternal() {
    if (!this.viewport)
      return;
    const t = this.gl, e = this.pointsProgram;
    t.useProgram(e);
    const r = t.getUniformLocation(
      e,
      "u_projectedGeoToViewportTransform"
    );
    t.uniformMatrix4fv(
      r,
      !1,
      g(this.viewport.projectedGeoToViewportTransform)
    );
    const o = t.getUniformLocation(
      e,
      "u_viewportToClipTransform"
    );
    t.uniformMatrix4fv(
      o,
      !1,
      g(this.viewport.viewportToClipTransform)
    );
    const i = t.getUniformLocation(
      e,
      "u_animationProgress"
    );
    t.uniform1f(i, this.animationProgress);
    for (const p of this.mapsWithRequestedTilesForViewport) {
      const n = this.warpedMapList.getWarpedMap(p);
      if (!n)
        continue;
      const a = n.pointLayers.reduce(
        (m, f) => m + f.projectedGeoPoints.length,
        0
      ), d = this.gl.POINTS, c = 0;
      t.bindVertexArray(n.pointsVao), t.drawArrays(d, c, a);
    }
  }
  setRenderOptionsUniforms(t, e) {
    const r = this.gl, o = this.mapsProgram, i = {
      removeColorOptions: {
        color: e.removeColorOptions?.color || t.removeColorOptions?.color,
        hardness: E(
          e.removeColorOptions?.hardness,
          t.removeColorOptions?.hardness
        ),
        threshold: E(
          e.removeColorOptions?.threshold,
          t.removeColorOptions?.threshold
        )
      },
      colorizeOptions: {
        ...t.colorizeOptions,
        ...e.colorizeOptions
      },
      gridOptions: {
        ...t.gridOptions,
        ...e.gridOptions
      }
    }, p = i.removeColorOptions?.color, n = r.getUniformLocation(o, "u_removeColor");
    if (r.uniform1f(n, p ? 1 : 0), p) {
      const f = r.getUniformLocation(
        o,
        "u_removeColorOptionsColor"
      );
      r.uniform3fv(f, p);
      const L = r.getUniformLocation(
        o,
        "u_removeColorOptionsThreshold"
      );
      r.uniform1f(
        L,
        i.removeColorOptions?.threshold || st
      );
      const s = r.getUniformLocation(
        o,
        "u_removeColorOptionsHardness"
      );
      r.uniform1f(
        s,
        i.removeColorOptions?.hardness || at
      );
    }
    const a = i.colorizeOptions?.color, d = r.getUniformLocation(o, "u_colorize");
    if (r.uniform1f(d, a ? 1 : 0), a) {
      const f = r.getUniformLocation(
        o,
        "u_colorizeOptionsColor"
      );
      r.uniform3fv(f, a);
    }
    const c = i.gridOptions?.enabled, m = r.getUniformLocation(o, "u_grid");
    r.uniform1f(m, c ? 1 : 0);
  }
  startTransformationTransition() {
    this.lastAnimationFrameRequestId !== void 0 && cancelAnimationFrame(this.lastAnimationFrameRequestId), this.animating = !0, this.transformationTransitionStart = void 0, this.lastAnimationFrameRequestId = requestAnimationFrame(
      this.transformationTransitionFrame.bind(this)
    );
  }
  transformationTransitionFrame(t) {
    if (this.transformationTransitionStart || (this.transformationTransitionStart = t), t - this.transformationTransitionStart < F)
      this.animationProgress = (t - this.transformationTransitionStart) / F, this.renderInternal(), this.lastAnimationFrameRequestId = requestAnimationFrame(
        this.transformationTransitionFrame.bind(this)
      );
    else {
      for (const e of this.warpedMapList.getWarpedMaps())
        e.resetPrevious();
      this.updateVertexBuffers(), this.animating = !1, this.animationProgress = 0, this.transformationTransitionStart = void 0, this.changed();
    }
  }
  changed() {
    this.dispatchEvent(new l(M.CHANGED));
  }
  imageInfoLoaded(t) {
    t instanceof l && this.dispatchEvent(new l(M.IMAGEINFOLOADED));
  }
  clearMap(t) {
    const e = this.warpedMapList.getWarpedMap(t);
    e && e.clearTextures();
  }
  mapTileLoaded(t) {
    if (t instanceof l) {
      const { mapId: e, tileUrl: r } = t.data, o = this.tileCache.getCacheableTile(r);
      if (!o || !o.isCachedTile())
        return;
      const i = this.warpedMapList.getWarpedMap(e);
      if (!i)
        return;
      i.addCachedTileAndUpdateTextures(o);
    }
  }
  mapTileRemoved(t) {
    if (t instanceof l) {
      const { mapId: e, tileUrl: r } = t.data, o = this.warpedMapList.getWarpedMap(e);
      if (!o)
        return;
      o.removeCachedTileAndUpdateTextures(r);
    }
  }
  warpedMapAdded(t) {
    if (t instanceof l) {
      const e = t.data, r = this.warpedMapList.getWarpedMap(e);
      r && this.addEventListenersToWebGL2WarpedMap(r);
    }
  }
  preChange(t) {
    if (t instanceof l) {
      const e = t.data;
      for (const r of this.warpedMapList.getWarpedMaps(e))
        this.animating && r.mixPreviousAndNew(1 - this.animationProgress);
    }
  }
  transformationChanged(t) {
    t instanceof l && (this.updateVertexBuffers(), this.startTransformationTransition());
  }
  distortionChanged(t) {
    t instanceof l && (this.updateVertexBuffers(), this.startTransformationTransition());
  }
  addEventListenersToWebGL2WarpedMap(t) {
    t.addEventListener(
      M.TEXTURESUPDATED,
      this.throttledChanged.bind(this)
    );
  }
  removeEventListenersFromWebGL2WarpedMap(t) {
    t.removeEventListener(
      M.TEXTURESUPDATED,
      this.throttledChanged.bind(this)
    );
  }
  contextLost() {
    this.disableRender = !0, this.cancelThrottledFunctions();
    for (const t of this.warpedMapList.getWarpedMaps())
      t.cancelThrottledFunctions();
    this.tileCache.clear();
  }
  contextRestored() {
    this.initializeWebGL(this.gl), this.disableRender = !1;
  }
}
export {
  St as default
};
//# sourceMappingURL=WebGL2Renderer.js.map
